/**
 * Kiosk mode — the whole viewport, no application chrome, for a report on a wall-mounted screen.
 *
 * `?kiosk=1` lives in the URL not because it is answer-defining (it is presentation, which §4.4
 * lets stay local) but because a wall screen is configured once as a bookmark and has no keyboard.
 * Never persisted to `localStorage`: a shared floor terminal must not trap the next person in a
 * chrome-less page. Fullscreen is best-effort — the API needs a gesture, which a bookmark has not
 * got, and the layout is already chrome-free without it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const KIOSK_PARAM = 'kiosk';

export interface KioskMode {
  /** True when the shell should stand down and the page should take the viewport. */
  isKiosk: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
}

function requestFullscreen(): Promise<void> {
  return document.documentElement.requestFullscreen?.() ?? Promise.resolve();
}

function exitFullscreen(): Promise<void> {
  return document.fullscreenElement === null
    ? Promise.resolve()
    : (document.exitFullscreen?.() ?? Promise.resolve());
}

export function useKioskMode(): KioskMode {
  const [search, setSearch] = useSearchParams();
  const isKiosk = search.get(KIOSK_PARAM) === '1';

  // Whether *we* entered fullscreen: a user already there via F11 or --kiosk must not have their
  // board silently exited the first time anything touches the API.
  const enteredFullscreen = useRef(false);

  const setKiosk = useCallback(
    (on: boolean) => {
      setSearch(
        (current) => {
          const params = new URLSearchParams(current);
          if (on) {
            params.set(KIOSK_PARAM, '1');
          } else {
            params.delete(KIOSK_PARAM);
          }
          return params;
        },
        // replace, not push: pushing would make Back toggle the chrome instead of leaving the report.
        { replace: true },
      );
    },
    [setSearch],
  );

  const enter = useCallback(() => {
    setKiosk(true);
    void requestFullscreen().then(
      () => {
        enteredFullscreen.current = true;
      },
      () => {
        // No gesture, or the browser refused. The chrome-free layout still applies.
        enteredFullscreen.current = false;
      },
    );
  }, [setKiosk]);

  const exit = useCallback(() => {
    setKiosk(false);
    if (enteredFullscreen.current) {
      enteredFullscreen.current = false;
      void exitFullscreen().catch(() => {
        /* already out, or refused — the layout is what matters */
      });
    }
  }, [setKiosk]);

  const toggle = useCallback(() => {
    if (isKiosk) {
      exit();
    } else {
      enter();
    }
  }, [isKiosk, enter, exit]);

  // Leaving fullscreen by any route brings the chrome back, or the reader is stranded with no
  // navigation and no obvious way out.
  useEffect(() => {
    if (!isKiosk) {
      return;
    }
    function onFullscreenChange() {
      if (document.fullscreenElement === null && enteredFullscreen.current) {
        enteredFullscreen.current = false;
        setKiosk(false);
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [isKiosk, setKiosk]);

  // Esc when fullscreen was never entered — the bookmark case, where there is nothing to escape
  // from but the reader still wants the menu back.
  useEffect(() => {
    if (!isKiosk) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && document.fullscreenElement === null) {
        setKiosk(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isKiosk, setKiosk]);

  return { isKiosk, enter, exit, toggle };
}

/**
 * A live clock for the kiosk bar: if it is moving, the page is. It answers "is this tab alive?",
 * never "how old is this data?" — that is `dataAsOf`'s, and the two must not be conflated (§5.7).
 */
export function useClock(): string {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
